import { validateObject, validateField } from "util/validation";
import { isBlank, notBlank } from "../../util/ext";
import {
  __,
  curry,
  isEmpty,
  map,
  mergeRight,
  pipe,
  reduce,
  reject,
} from "ramda";

export const ProductIdentifierConstraints = {
  DOI: {
    format: {
      pattern:
        "^(http[s]?://(dx\\.)?doi.org/|doi)?((10[.][0-9]{4,}[-._;()/:a-zA-Z0-9]+$)$)",
      message: "^Not a valid DOI value",
    },
  },
  "GTIN-13": {
    length: { is: 13 },
    format: {
      pattern: "[0-9]{13}",
    },
  },
  "GTIN-14": {
    length: { is: 14 },
    format: {
      pattern: "[0-9]{14}",
    },
  },
  "ISBN-13": {
    length: { is: 13 },
    format: {
      pattern: "[0-9]{13}",
    },
  },
  "ISBN-10": {
    length: { is: 10 },
    format: {
      pattern: "[0-9]{10}",
    },
  },
  ISSN: {
    length: { is: 8 },
    format: {
      pattern: "[0-9]{8}",
    },
  },
};

const contentConstraints = {
  title: {
    presence: {
      allowEmpty: false,
      message: "^Title must not be blank",
    },
  },
};

const productIdentifierConstraints = (type, code) => {
  let baseConstraint = {
    presence: {
      allowEmpty: false,
      message: "^Identifier must not be blank",
    },
  };

  const typeConstraint = ProductIdentifierConstraints?.[type] ?? {};
  return {
    [code]: mergeRight(baseConstraint, typeConstraint),
  };
};

const typeConstraints = {
  AUDIOBOOK: {
    bisacCategories: {
      presence: {
        allowEmpty: false,
        message: "^At least one category must be selected",
      },
    },
    contributors: {
      presence: {
        allowEmpty: false,
        message: "^At least one contributor must be added",
      },
    },
  },
  BOOK: {
    bisacCategories: {
      presence: {
        allowEmpty: false,
        message: "^At least one category must be selected",
      },
    },
    contributors: {
      presence: {
        allowEmpty: false,
        message: "^At least one contributor must be added",
      },
    },
  },
  IMAGE: {
    altText: {
      presence: {
        allowEmpty: false,
        message: "^An image must be provided with alt text",
      },
    },
  },
};

const contributorConstraints = type => {
  let constraints = {
    role: {
      presence: {
        allowEmpty: false,
        message: "^You must select a role",
      },
    },
    website: {
      presence: false,
      format: {
        pattern: /^[^ "]+$/,
        message: "^Invalid website",
      },
    },
    twitterHandle: {
      presence: false,
      format: {
        pattern: /^@[A-Za-z0-9_]+$/,
        message: "^Invalid X handle",
      },
    },
  };

  if (type === "PERSON") {
    constraints = mergeRight(
      {
        lastName: {
          presence: {
            allowEmpty: false,
            message: "^Last name is required",
          },
        },
      },
      constraints,
    );
  } else {
    constraints = mergeRight(
      {
        fullName: {
          presence: {
            allowEmpty: false,
            message: "^Corporation name is required",
          },
        },
      },
      constraints,
    );
  }

  return constraints;
};

export const getConstraints = type =>
  mergeRight(contentConstraints, typeConstraints[type]);

export const validateContentField = (type, field, value, errors = {}) =>
  validateField(getConstraints(type), field, value, errors);

export const validateContent = curry((type, fields) =>
  validateObject(getConstraints(type), fields),
);

export const validateContributorField = (type, field, value, errors = {}) =>
  validateField(contributorConstraints(type), field, value, errors);

export const validateContributor = curry((type, fields) =>
  validateObject(contributorConstraints(type), fields),
);

export const validateProductIdentifierField = (
  type,
  code,
  value,
  errors = {},
) =>
  validateField(productIdentifierConstraints(type, code), code, value, errors);

export const validateProductIdentifier = curry((type, code, fields) =>
  validateObject(productIdentifierConstraints(type, code), fields),
);

export const validateProductIdentifiers = productIdentifiers => {
  if (isEmpty(productIdentifiers)) {
    return {};
  }

  const typeToErrors = pipe(
    map(pi => {
      const error = validateProductIdentifier(pi.type, "value", pi);
      if (error) {
        return {
          [pi.code]: firstError(error, "value"),
        };
      }
    }),
    reject(isBlank),
  )(productIdentifiers);

  if (isEmpty(typeToErrors)) {
    return {};
  }

  const errors = reduce(
    (acc, error) => {
      console.log(Object.entries(error));
      const [pairs] = Object.entries(error);
      return {
        ...acc,
        [pairs[0]]: [pairs[1]],
      };
    },
    {},
    typeToErrors,
  );

  return errors;
};

export const hasError = curry((errors, field) => notBlank(errors[field]));

export const firstError = curry((errors, field) =>
  errors[field] ? errors[field][0] : "",
);
